import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, 
  Home,
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  Image as ImageIcon,
  MapPin,
  IndianRupee,
  Building
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import api from "../lib/api";
import { ImagesField } from './ui/images-field';
import { useNavigate } from 'react-router-dom';


const PropertyManagement = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location: 'Sector Chi V, Greater Noida',
    propertyType: 'Apartment',
    listingType: 'sale',
    bhk: '',
    price: '',
    area: '',
    status: 'available',
    description: '',
    images: [],
    amenities: '',
    highlights: '',
    featured: false,
    ownerName: '',
    ownerPhone: '',
    ownerEmail: ''
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/properties`);
      setProperties(response.data.items || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Prepare data
      const propertyData = {
        ...formData,
        images: formData.images,
        amenities: formData.amenities.split('\n').filter(a => a.trim()),
        highlights: formData.highlights.split('\n').filter(h => h.trim())
      };

      if (editingProperty) {
        // Update existing property
        await api.patch(`/properties/${editingProperty.id}`, propertyData);
        toast.success('Property updated successfully!');
      } else {
        // Create new property
        await api.post(`/properties`, propertyData);
        toast.success('Property created successfully!');
      }

      setShowForm(false);
      setEditingProperty(null);
      resetForm();
      fetchProperties();
    } catch (error) {
      console.error('Error saving property:', error);
      toast.error(error.response?.data?.detail || 'Failed to save property');
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      name: property.name,
      location: property.location,
      propertyType: property.propertyType,
      listingType: property.listingType || 'sale',
      bhk: property.bhk || '',
      price: property.price,
      area: property.area,
      status: property.status,
      description: property.description,
      images: property.images || [],
      amenities: property.amenities.join('\n'),
      highlights: property.highlights.join('\n'),
      featured: property.featured,
      ownerName: property.ownerName || '',
      ownerPhone: property.ownerPhone || '',
      ownerEmail: property.ownerEmail || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (propertyId, propertyName) => {
    if (!window.confirm(`Are you sure you want to delete "${propertyName}"?`)) {
      return;
    }

    try {
      await api.delete(`/properties/${propertyId}`);
      toast.success('Property deleted successfully!');
      fetchProperties();
    } catch (error) {
      console.error('Error deleting property:', error);
      toast.error('Failed to delete property');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: 'Sector Chi V, Greater Noida',
      propertyType: 'Apartment',
      listingType: 'sale',
      bhk: '',
      price: '',
      area: '',
      status: 'available',
      description: '',
      images: [],
      amenities: '',
      highlights: '',
      featured: false,
      ownerName: '',
      ownerPhone: '',
      ownerEmail: ''
    });
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingProperty(null);
    resetForm();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { color: 'bg-green-100 text-green-800', label: 'Available' },
      sold: { color: 'bg-red-100 text-red-800', label: 'Sold' },
      under_construction: { color: 'bg-yellow-100 text-yellow-800', label: 'Under Construction' }
    };
    return statusConfig[status] || statusConfig.available;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading properties...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/admin/dashboard')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Property Management</h1>
                <p className="text-sm text-gray-600">Add, edit, and manage property listings</p>
              </div>
            </div>
            <Button onClick={() => navigate('/properties')} variant="outline">
              <Home className="h-4 w-4 mr-2" />
              View Website
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Add Property Button */}
        {!showForm && (
          <div className="mb-6">
            <Button onClick={() => setShowForm(true)} size="lg" className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-5 w-5 mr-2" />
              Add New Property
            </Button>
          </div>
        )}

        {/* Property Form */}
        {showForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>{editingProperty ? 'Edit Property' : 'Add New Property'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Property Name *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="e.g., Luxury 3BHK Apartment"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Location *</label>
                    <Input
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      placeholder="e.g., Sector Chi V, Greater Noida"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Listing Type *</label>
                    <div className="flex gap-2">
                      {[
                        { value: 'sale', label: 'For Sale' },
                        { value: 'rent', label: 'For Rent' },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, listingType: opt.value })}
                          className={`flex-1 px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                            formData.listingType === opt.value
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                          data-testid={`listing-type-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Property Type *</label>
                    <Select name="propertyType" value={formData.propertyType} onValueChange={(value) => setFormData({...formData, propertyType: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Apartment">Apartment</SelectItem>
                        <SelectItem value="Villa">Villa</SelectItem>
                        <SelectItem value="Penthouse">Penthouse</SelectItem>
                        <SelectItem value="Commercial">Commercial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Configuration (BHK)</label>
                    <Input
                      name="bhk"
                      value={formData.bhk}
                      onChange={handleInputChange}
                      placeholder="e.g., 3BHK, 2BHK"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      {formData.listingType === 'rent' ? 'Monthly Rent *' : 'Selling Price *'}
                    </label>
                    <Input
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder={formData.listingType === 'rent' ? 'e.g., 15,000/mo' : 'e.g., 65 Lac'}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Area *</label>
                    <Input
                      name="area"
                      value={formData.area}
                      onChange={handleInputChange}
                      placeholder="e.g., 1450 sq.ft"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Status *</label>
                    <Select name="status" value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="under_construction">Under Construction</SelectItem>
                        <SelectItem value="sold">Sold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        name="featured"
                        checked={formData.featured}
                        onChange={handleInputChange}
                        className="rounded border-gray-300"
                      />
                      <span className="text-sm font-medium text-gray-700">Mark as Featured</span>
                    </label>
                  </div>
                </div>

                {/* <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Description *</label>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Detailed property description..."
                    rows={4}
                    required
                  />
                </div> */}

                <div className="space-y-4 border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-900">Owner Contact Details (Optional)</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Owner Name</label>
                      <Input
                        name="ownerName"
                        value={formData.ownerName}
                        onChange={handleInputChange}
                        placeholder="Owner Full Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Owner Phone</label>
                      <Input
                        name="ownerPhone"
                        value={formData.ownerPhone}
                        onChange={handleInputChange}
                        placeholder="e.g. 9876543210"
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-sm font-medium text-gray-700">Owner Email</label>
                      <Input
                        name="ownerEmail"
                        type="email"
                        value={formData.ownerEmail}
                        onChange={handleInputChange}
                        placeholder="owner@email.com"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Property Images</label>
                  <ImagesField
                    value={formData.images}
                    onChange={(urls) => setFormData((f) => ({ ...f, images: urls }))}
                    kind="property"
                    max={20}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Amenities (one per line)</label>
                  <Textarea
                    name="amenities"
                    value={formData.amenities}
                    onChange={handleInputChange}
                    placeholder="24/7 Security&#10;Power Backup&#10;Lift&#10;Parking"
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Highlights (one per line)</label>
                  <Textarea
                    name="highlights"
                    value={formData.highlights}
                    onChange={handleInputChange}
                    placeholder="Vastu Compliant&#10;Gated Community&#10;Near Metro Station"
                    rows={4}
                  />
                </div>

                <div className="flex gap-4">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    <Save className="h-4 w-4 mr-2" />
                    {editingProperty ? 'Update Property' : 'Create Property'}
                  </Button>
                  <Button type="button" variant="outline" onClick={cancelForm}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Properties List */}
        <Card>
          <CardHeader>
            <CardTitle>All Properties ({properties.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {properties.length === 0 ? (
              <div className="text-center py-12">
                <Home className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No properties added yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {properties.map((property) => {
                  const statusConfig = getStatusBadge(property.status);
                  return (
                    <Card key={property.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex gap-4 flex-1">
                            {property.images[0] && (
                              <img
                                src={property.images[0]}
                                alt={property.name}
                                className="w-32 h-32 object-cover rounded-lg"
                              />
                            )}
                            <div className="flex-1">
                              <div className="flex items-start gap-3 mb-2 flex-wrap">
                                <h3 className="text-lg font-semibold text-gray-900">{property.name}</h3>
                                <Badge className={property.listingType === 'rent' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
                                  {property.listingType === 'rent' ? 'For Rent' : 'For Sale'}
                                </Badge>
                                <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                                {property.featured && (
                                  <Badge className="bg-amber-500 text-white">Featured</Badge>
                                )}
                              </div>
                              <div className="space-y-1 text-sm text-gray-600">
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-4 w-4 text-blue-600" />
                                  {property.location}
                                </div>
                                <div className="flex items-center gap-4">
                                  {property.bhk && (
                                    <span className="flex items-center gap-1">
                                      <Building className="h-4 w-4 text-blue-600" />
                                      {property.bhk}
                                    </span>
                                  )}
                                  <span>{property.area}</span>
                                  <span className="flex items-center gap-1">
                                    <IndianRupee className="h-4 w-4 text-blue-600" />
                                    <strong>
                                      {property.price}
                                      {property.listingType === 'rent' && ' / month'}
                                    </strong>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleEdit(property)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(property.id, property.name)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PropertyManagement;
